import type { PeekViewService } from '@affine/core/modules/peek-view';
import { I18n } from '@affine/i18n';
import { WithDisposable } from '@blocksuite/global/lit';
import { PageIcon, ViewIcon } from '@blocksuite/icons/lit';
import { ShadowlessElement } from '@blocksuite/std';
import type { Signal } from '@preact/signals-core';
import { html, nothing } from 'lit';
import { property } from 'lit/decorators.js';

import {
  localizedToolCallName,
  localizedToolResultName,
  localizedToolText,
} from '../../utils/stream-objects';

interface DocReadToolCall {
  type: 'tool-call';
  toolCallId: string;
  toolName: string;
  args: { doc_id: string };
}

interface DocReadToolResult {
  type: 'tool-result';
  toolCallId: string;
  toolName: string;
  args: { doc_id: string };
  result:
    | { docId?: string; title: string; markdown: string }
    | {
        success?: boolean;
        data: { docId?: string; title: string; markdown: string };
      };
}

export class DocReadResult extends WithDisposable(ShadowlessElement) {
  @property({ attribute: false })
  accessor data!: DocReadToolCall | DocReadToolResult;

  @property({ attribute: false })
  accessor width: Signal<number | undefined> | undefined;

  @property({ attribute: false })
  accessor peekViewService!: PeekViewService;

  renderToolCall() {
    // TODO: get document name by doc_id
    return html`<tool-call-card
      .name=${localizedToolCallName('doc_read')}
      .icon=${ViewIcon()}
      .width=${this.width}
    ></tool-call-card>`;
  }

  renderToolResult() {
    if (this.data.type !== 'tool-result') {
      return nothing;
    }
    // Handle both direct result format and { success, data } wrapper
    const rawResult = this.data.result;
    const result =
      rawResult && 'data' in rawResult ? rawResult.data : rawResult;
    const title =
      result?.title ?? I18n.t('com.streamlain.ai.doc-search.untitled');
    const markdown = result?.markdown ?? '';
    const docId = result?.docId;

    return html`<tool-result-card
      .name=${localizedToolText(
        'com.streamlain.ai.tool.doc_read.completed-title',
        `Read "${title}"`,
        {
          title,
          tool: localizedToolResultName('doc_read', 'completed'),
        }
      )}
      .icon=${ViewIcon()}
      .width=${this.width}
      .results=${[
        {
          title,
          icon: PageIcon(),
          content: markdown,
          onClick: () => {
            if (!docId) {
              return;
            }
            this.peekViewService.peekView
              .open({
                type: 'doc',
                docRef: { docId },
              })
              .catch(console.error);
          },
        },
      ]}
    ></tool-result-card>`;
  }

  protected override render() {
    if (this.data.type === 'tool-call') {
      return this.renderToolCall();
    }
    if (this.data.type === 'tool-result') {
      return this.renderToolResult();
    }
    return nothing;
  }
}
